import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import { camelCase } from "lodash";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Button from "@shared/v2/Button";
import DatePicker from "@shared/v2/DatePicker";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import Modal, { Prompt } from "@shared/v2/Modal";
import RadioButton from "@shared/v2/RadioButton";
import TextInput from "@shared/v2/TextInput";
import Tooltip from "@shared/v2/Tooltip";
import { CircleInfoSolidV6 } from "@shared/v2/Icomoon";
import MlsListingDropdown from "@shared/MlsListingDropdown";
import useMlsListingDetails from "@shared/MlsListingDropdown/useMlsListingDetails";
import Loading from "../../../Goals/components/Loader";
import ListingPreviewCard from "../../../Transactions/ListingPreviewCard";
import HorizontalLine from "./HorizontalRule";
import useTimeOptions from "./hooks/useTimeOptions";
import useTags from "./hooks/useTags";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";

const validateForm = ({ openHouse, propertyDetails } = { openHouse: {}, propertyDetails: {} }) => {
  const errors = new Map();
  if (!openHouse.startDate) errors.set("startDate", "Date is required");
  if (!propertyDetails.streetAddress) errors.set("streetAddress", "Address is required");
  if (!propertyDetails.city) errors.set("city", "City is required");
  if (!propertyDetails.state) errors.set("state", "State is required");
  if (!propertyDetails.postalCode) errors.set("postalCode", "ZIP / Postal Code is required");

  return errors;
};

const useOpenHouseApi = () => {
  const [loading, setLoading] = useState(false);
  return {
    actions: {
      delete: (openHouseUuid) => {
        setLoading(true);
        return caseTransformingAxios
          .delete("/api/v4/reporting/open_house_dashboard", { params: { uuids: [openHouseUuid] } })
          .finally(() => setLoading(false));
      },
      save: (openHouseData) => {
        const isEdit = openHouseData?.openHouse?.uuid;
        setLoading(true);
        return caseTransformingAxios[isEdit ? "patch" : "post"]("/api/v4/reporting/open_house_dashboard", {
          ...openHouseData,
          openHouse: {
            ...openHouseData.openHouse,
            propertyId: openHouseData.openHouse.propertyId || undefined,
          },
        }).finally(() => setLoading(false));
      },
    },
    loading,
  };
};

const OpenHouseModal = ({ hosts, onClose, openHouse: originalOpenHouse, formOptions }) => {
  const [showDelete, setShowDelete] = useState(false);
  const currentUser = useCurrentUser();
  const openHouseApi = useOpenHouseApi();
  const [openHouseData, setOpenHouseData] = useState(originalOpenHouse);
  const { openHouse, propertyDetails, tags } = openHouseData || {};
  const [mlsListing, setMlsListing] = useState(null);
  const [errors, setErrors] = useState(new Map());
  const isEdit = Boolean(openHouse?.uuid);
  const mlsListingDetails = useMlsListingDetails(mlsListing?.value);
  const timeOptions = useTimeOptions();
  const availableTags = useTags();
  const previousAddressTagRef = useRef(propertyDetails?.streetAddress || "");

  const selectedTags = useMemo(() => {
    const newTags = (tags || []).map((tag) => {
      const isAddressTag = tag === previousAddressTagRef.current;
      return {
        label: tag,
        value: tag,
        meta: { isDisabled: tag === "Open House" || tag === "openhouseapp" || isAddressTag },
      };
    });
    return newTags;
  }, [tags]);

  useEffect(() => {
    if (!originalOpenHouse) {
      setMlsListing(null);
      setErrors(new Map());
    } else if (originalOpenHouse.openHouse.propertyId) {
      setMlsListing({ value: originalOpenHouse.openHouse.propertyId });
    }

    if (originalOpenHouse) {
      setOpenHouseData({
        ...originalOpenHouse,
        openHouse: {
          ...originalOpenHouse.openHouse,
          formUuid: originalOpenHouse.openHouse.formUuid || formOptions[0]?.uuid,
        },
      });
    } else {
      setOpenHouseData(null);
    }
  }, [originalOpenHouse, formOptions]);

  useEffect(() => {
    const addressTag = propertyDetails?.streetAddress || "";
    setOpenHouseData((prev) => {
      const newData = prev
        ? {
            ...prev,
            tags: prev.tags?.map((tag) => (tag === previousAddressTagRef.current ? addressTag : tag)),
          }
        : null;

      previousAddressTagRef.current = addressTag;

      return newData;
    });
  }, [propertyDetails?.streetAddress]);

  useEffect(() => {
    setOpenHouseData((prev) =>
      prev
        ? {
            ...prev,
            openHouse: {
              ...prev.openHouse,
              propertyId: mlsListingDetails.data?.blossorId || prev.openHouse.propertyId,
            },
            propertyDetails: mlsListingDetails.data
              ? {
                  ...prev.propertyDetails,
                  streetAddress: mlsListingDetails.data?.streetAddress,
                  city: mlsListingDetails.data?.city,
                  state: mlsListingDetails.data?.state,
                  postalCode: mlsListingDetails.data?.postalCode,
                }
              : originalOpenHouse?.propertyDetails,
          }
        : null,
    );
  }, [mlsListingDetails.data]);

  const updateOpenHouseDetail = (key) => (value) =>
    setOpenHouseData((prev) => (prev ? { ...prev, openHouse: { ...prev.openHouse, [key]: value } } : null));

  const updatePropertyDetail = (key) => (e) => {
    const { value } = e.target;
    setOpenHouseData((prev) =>
      prev ? { ...prev, propertyDetails: { ...prev.propertyDetails, [key]: value } } : null,
    );
  };

  return (
    <>
      <Prompt
        closeOnEscape={false}
        containerClassName="!tw-z-[3001]"
        title="Delete Open House"
        description="Open house will be deleted from your Brivity Open House app. Are you sure you want to proceed?"
        onCancel={() => setShowDelete(false)}
        onPrimary={() => {
          openHouseApi.actions.delete(originalOpenHouse.openHouse.uuid).then(() => {
            onClose();
            setShowDelete(false);
          });
        }}
        cancelProps={{ isDisabled: openHouseApi.loading }}
        isPrimaryLoading={openHouseApi.loading}
        primaryOption="Yes, Delete"
        primaryProps={{ schema: "warning" }}
        show={showDelete}
      />
      <Modal
        containerClassName="!tw-z-[3001]"
        className="tw-flex tw-items-center tw-justify-center"
        contentClassName="tw-max-w-[620px] tw-w-[100vw] tw-max-h-[85vh] tw-flex tw-flex-col tw-gap-[32px]"
        show={!showDelete && Boolean(originalOpenHouse)}
        onHide={onClose}
        closeOnEscape={false}
      >
        <Modal.Header
          className="tw-px-[8px]"
          title={`${isEdit ? "Edit" : "Create"} Open House`}
          description={
            <span className="tw-text-gray-50">
              {isEdit
                ? "Changes made here will update the open house in your Brivity Open House app."
                : "Creates an open house in your Brivity Open House app. Import a listing or fill out the listing information below manually."}
            </span>
          }
          closeButton={!openHouseApi.loading}
        />

        <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-overflow-auto tw-gap-[24px] tw-px-[8px]">
          <HorizontalLine oversized />
          {!mlsListingDetails.data && (
            <MlsListingDropdown
              label="Import Listing Details"
              placeholder="Search by address or MLS # to auto-populate listing data below"
              value={mlsListing}
              onChange={setMlsListing}
            />
          )}
          {mlsListingDetails.data && (
            <ListingPreviewCard
              listingData={mlsListingDetails.data}
              clearListing={() => {
                mlsListingDetails.actions.clear();
                setMlsListing(null);
              }}
            />
          )}
          {mlsListingDetails.loading && <Loading />}
          <HorizontalLine />
          <TextInput
            label="Address"
            isRequired
            value={propertyDetails?.streetAddress || ""}
            onChange={updatePropertyDetail("streetAddress")}
            error={errors.get("streetAddress")}
          />
          <TextInput
            label="City"
            isRequired
            value={propertyDetails?.city || ""}
            onChange={updatePropertyDetail("city")}
            error={errors.get("city")}
          />
          <div className="tw-flex tw-gap-[24px]">
            <TextInput
              containerClassName="tw-flex-1"
              label="State / Province"
              isRequired
              value={propertyDetails?.state || ""}
              onChange={updatePropertyDetail("state")}
              error={errors.get("state")}
            />
            <TextInput
              containerClassName="tw-flex-1"
              label="ZIP / Postal Code"
              isRequired
              value={propertyDetails?.postalCode || ""}
              onChange={updatePropertyDetail("postalCode")}
              error={errors.get("postalCode")}
            />
          </div>

          <HorizontalLine />

          <div className="tw-flex tw-items-start tw-gap-[24px]">
            <DatePicker
              containerClassName="tw-flex-1"
              inputProps={{
                label: "Date",
                isRequired: true,
                error: errors.get("startDate"),
              }}
              value={openHouse?.startDate}
              onChange={(startDate) => {
                if (startDate?.indexOf("/") > 0) {
                  const date = dayjs(startDate, "MM/DD/YYYY", true);
                  if (date.isValid()) {
                    updateOpenHouseDetail("startDate")(date.format("YYYY-MM-DD"));
                    updateOpenHouseDetail("endDate")(date.format("YYYY-MM-DD"));
                  }
                }
              }}
            />
            <div className="tw-flex-1 tw-flex tw-items-end tw-gap-[24px]">
              <Dropdown
                containerClassName="tw-flex-1"
                label="Time"
                isRequired
                options={timeOptions}
                value={timeOptions.find((t) => t.value === openHouse?.startTime) || null}
                onChange={(startTime) => updateOpenHouseDetail("startTime")(startTime.value)}
                menuShouldComeToFront
              />
              <Dropdown
                containerClassName="tw-flex-1"
                options={timeOptions}
                value={timeOptions.find((t) => t.value === openHouse?.endTime) || null}
                onChange={(endTime) => updateOpenHouseDetail("endTime")(endTime.value)}
                menuShouldComeToFront
              />
            </div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            <FieldLabel
              label={
                <div className="tw-flex tw-items-center tw-gap-[4px]">
                  <span>Registration Form</span>
                  <Tooltip
                    multiline
                    tooltipClassName="tw-z-[10000]"
                    innerClassName="tw-max-w-[250px] tw-flex tw-flex-col tw-gap-[16px]"
                    trigger={<CircleInfoSolidV6 className="tw-text-gray-75" />}
                    content={
                      <>
                        <div>
                          <b>Light Registration:</b> Collects just the essentials at check-in: name, contact
                          info, and whether they’re already working with an agent.
                        </div>
                        <div>
                          <b>Full Registration:</b> Gathers additional details about the attendee’s real
                          estate goals.
                        </div>
                      </>
                    }
                  />
                </div>
              }
            />
            <div className="tw-flex tw-gap-[16px]">
              {formOptions.map((option) => (
                <RadioButton
                  key={option.uuid}
                  name="formUuid"
                  label={option.formName}
                  value={option.uuid}
                  checked={openHouse?.formUuid === option.uuid}
                  onChange={() => updateOpenHouseDetail("formUuid")(option.uuid)}
                />
              ))}
            </div>
          </div>

          {currentUser.account.isAdmin && (
            <Dropdown
              label={<div className="tw-flex tw-items-center tw-gap-[4px]">Host</div>}
              isSearchable
              options={hosts}
              value={hosts.find((h) => h.value === openHouse?.hostPersonUuid) || null}
              onChange={(host) => updateOpenHouseDetail("hostPersonUuid")(host.value)}
              menuShouldComeToFront
            />
          )}
          <Dropdown
            label="Tags"
            placeholder="Search tags"
            isMulti
            isSearchable
            menuShouldComeToFront
            isOptionDisabled={(option, value) => option === value[0] || option === value[1]}
            options={availableTags}
            value={selectedTags}
            onChange={(newTags) =>
              setOpenHouseData((prev) => ({ ...prev, tags: newTags.map((t) => t.value) }))
            }
          />
          <TextInput
            label="Notes"
            multiline
            value={openHouse?.instructions || ""}
            onChange={(e) => updateOpenHouseDetail("instructions")(e.target.value)}
          />
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between">
          <Button disabled={openHouseApi.loading} schema="tertiary" size="medium" onClick={onClose}>
            Cancel
          </Button>
          <div className="tw-flex tw-gap-[8px]">
            {isEdit && (
              <Button
                disabled={openHouseApi.loading}
                schema="secondary"
                size="medium"
                onClick={() => setShowDelete(true)}
              >
                Delete Open House
              </Button>
            )}
            <Button
              isLoading={openHouseApi.loading}
              schema="primary"
              size="medium"
              onClick={() => {
                const err = validateForm(openHouseData);
                setErrors(err);
                if (!err.size) {
                  openHouseApi.actions
                    .save(openHouseData)
                    .then(onClose)
                    .catch((axiosErr) => {
                      const newErrors = new Map();
                      if (axiosErr.response?.data?.errors) {
                        axiosErr.response.data.errors.forEach((e) => {
                          newErrors.set(camelCase(e.path.replace(/\w+\./g, "")), e.msg);
                        });
                      }
                      setErrors(newErrors);
                    });
                }
              }}
            >
              {isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

OpenHouseModal.propTypes = {
  hosts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onClose: PropTypes.func.isRequired,
  openHouse: PropTypes.shape(),
  formOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

OpenHouseModal.defaultProps = {
  openHouse: null,
};

export default OpenHouseModal;
